/**
 * Расчёты «Обзора района» (agroai-0yt.8) — без разметки, чтобы их можно было
 * проверить тестом: сравнения, медиана массива, темп кампании, накопленная
 * доля по неделям, тепловая карта массив × стадия, очаги и порядок поручений.
 *
 * Общее правило всех функций: отсутствие — не ноль. Если сервер не назвал
 * величину (`null`) или знаменатель пуст, результат — `null`, и экран пишет
 * «—», а не «0».
 */
import type { Schemas } from '../../../api/client';
import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';

const names = defineMessages({
  massifUnnamed: {
    id: 'district.names.massifUnnamed',
    defaultMessage: 'массив не назван',
    description: 'Подпись, когда у массива нет названия в данных (строчными: встаёт в заголовок и фильтр на экране «Обзор района»; хоким, менеджер)',
  },
  farmUnnamed: {
    id: 'district.names.farmUnnamed',
    defaultMessage: 'хозяйство без имени',
    description: 'Подпись, когда у хозяйства нет имени в данных (строчными; в списках и подсказках «Обзора района»)',
  },
});

type Measured = Schemas['MeasuredOut'];
type Progress = Schemas['ProgressCount'];
export type FarmRow = Schemas['SeasonBreakdownRowOut'];
export type WeekRow = Schemas['SeasonWeekOut'];
export type DayRow = Schemas['SeasonDayOut'];
export type MassifStageRow = Schemas['SeasonMassifStageOut'];
export type WaterRow = Schemas['WaterSummaryRowOut'];
export type QRow = Schemas['QStatusRow'];
export type Assignment = Schemas['AssignmentOut'];

/** Порог ранжирования хозяйств: меньше — доля слишком шумная. */
export const MIN_OBLIGATIONS = 30;
/** Канал — очаг, если подано меньше этой доли запрошенного. */
export const WATER_HOTSPOT_PCT = 85;

export const value = (m: Measured | null | undefined): number | null =>
  m && m.value !== null && Number.isFinite(m.value) ? m.value : null;

/** Доля в процентах; пустой знаменатель — `null`. */
export function sharePct(part: number | null, total: number | null): number | null {
  if (part === null || total === null || !(total > 0)) return null;
  return (100 * part) / total;
}

export function median(values: readonly number[]): number | null {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
}

/* ------------------------------------------------------------ даты */

export function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number) as [number, number, number];
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return t.toISOString().slice(0, 10);
}

export function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from.slice(0, 10)}T00:00:00Z`);
  const b = Date.parse(`${to.slice(0, 10)}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

/* ------------------------------------------------------------ сейчас и скоро */

interface Windowed {
  window_from: string | null;
  window_to: string | null;
}

/**
 * Работы с открытым на дату окном (начало ≤ даты ≤ конец), по ближнему концу
 * окна — первыми те, что раньше уйдут в «после срока». Без дат окна работа
 * не попадает никуда: «не знаем» не равно «идёт».
 */
export function openWindows<T extends Windowed>(works: readonly T[], asOf: string): T[] {
  return works
    .filter(w => w.window_from && w.window_to && w.window_from <= asOf && w.window_to >= asOf)
    .sort((a, b) => (a.window_to ?? '').localeCompare(b.window_to ?? ''));
}

/** Работы, чьё окно начнётся в ближайшие `days` дней после даты (дата исключена), по началу. */
export function upcomingWindows<T extends Windowed>(works: readonly T[], asOf: string, days = 14): T[] {
  const until = addDays(asOf, days);
  return works
    .filter(w => w.window_from !== null && w.window_from > asOf && w.window_from <= until)
    .sort((a, b) => (a.window_from ?? '').localeCompare(b.window_from ?? ''));
}

/* ------------------------------------------------------------ имена */

/** «А.Навоий массиви» → «А.Навоий»: слово «массив» уже стоит в заголовке. */
export function massifLabel(name: string | null | undefined): string {
  if (!name) return tr(names.massifUnnamed);
  return name.replace(/\s+массиви\s*$/i, '').replace(/\s+/g, ' ').trim();
}

export function farmLabel(name: string | null | undefined): string {
  return name ? name.replace(/\s+/g, ' ').trim() : tr(names.farmUnnamed);
}

/* ------------------------------------------------------------ выполнено */

/**
 * «Выполнено» обязательств (agroai-6al.35, журнал №290): отчёт «выполнено».
 * Сервер, не считавший степень (`done === null`), — прежняя ось «отчитались»:
 * так выдача остаётся читаемой, а не обнуляется.
 */
export function doneOf(p: Pick<Progress, 'reported'> & { done?: number | null }): number {
  return typeof p.done === 'number' ? p.done : p.reported;
}

/** «В процессе» (отчёт есть, финального нет); `null` — сервер не считал. */
export function inProgressOf(p: { in_progress?: number | null }): number | null {
  return typeof p.in_progress === 'number' ? p.in_progress : null;
}

/** Считал ли сервер степень выполнения у этого знаменателя. */
export function hasProgressSplit(p: { done?: number | null; in_progress?: number | null } | null | undefined): boolean {
  return !!p && typeof p.done === 'number' && typeof p.in_progress === 'number';
}

/* ------------------------------------------------------------ KPI */

export interface ShareDelta {
  /** Текущая доля, %. */
  share: number | null;
  /** Разница в процентных пунктах; `null` — сравнивать не с чем. */
  delta: number | null;
}

/**
 * Доля выполненного и её изменение к другой точке (неделя назад, прошлый
 * сезон). Числитель — «выполнено» (`doneOf`): отчёт «в процессе» не делает
 * обязательство выполненным (agroai-6al.35).
 */
export function shareDelta(now: Progress | null | undefined, then: Progress | null | undefined): ShareDelta {
  const share = now ? sharePct(doneOf(now), now.total) : null;
  const before = then ? sharePct(doneOf(then), then.total) : null;
  return { share, delta: share !== null && before !== null ? share - before : null };
}

export function countDelta(now: number | null, then: number | null): number | null {
  return now !== null && then !== null ? now - then : null;
}

/* ------------------------------------------------------------ ход сезона */

export interface SeasonPoint {
  week: string;
  weekFrom: string;
  /** Все отчёты недели (дата выполнения в отчёте); `null` — сервер не назвал. */
  weekReported: number | null;
  /** Из них отчёты «выполнено»; без разреза сервера — все отчёты недели. */
  weekDone: number | null;
  /** Из них отчёты «в процессе»; `null` — сервер степень не считал. */
  weekInProgress: number | null;
  /** Накопленная доля выполненных обязательств цикла на конец недели, %. */
  share: number | null;
  /** Та же доля прошлого сезона на том же дне от начала цикла. */
  prevShare: number | null;
  /** Неделя ещё не закрыта на дату среза. */
  open: boolean;
}

/**
 * Накопленная доля по неделям. Сервер отдаёт итог (`reported`) и отчёты
 * последних N недель; база накопления — то, что было до первой недели ряда:
 * `reported − Σweekly`. Знаменатель — обязательства цикла на дату среза.
 */
export function cumulativeShares(
  weekly: readonly WeekRow[],
  reported: number,
  total: number,
  axis: 'reported' | 'done' = 'reported',
): Array<number | null> {
  const counts = weekly.map(w => (axis === 'done' && w.done ? value(w.done) : value(w.reported)));
  if (counts.some(c => c === null) || !(total > 0)) return weekly.map(() => null);
  const sum = (counts as number[]).reduce((a, b) => a + b, 0);
  let acc = reported - sum;
  return (counts as number[]).map(c => {
    acc += c;
    return (100 * acc) / total;
  });
}

/**
 * Ряд «Ход работ сезона»: накопленная доля ВЫПОЛНЕННЫХ обязательств и отчёты
 * недели с разрезом «выполнено / в процессе» (agroai-6al.35). `done` — итог
 * «выполнено» на дату среза; без него ряд строится по оси «отчитались».
 */
export function seasonSeries(
  current: { weekly: readonly WeekRow[]; reported: number; done?: number | null; total: number; asOf: string },
  previous?: { weekly: readonly WeekRow[]; reported: number; done?: number | null; total: number } | null,
): SeasonPoint[] {
  const split = typeof current.done === 'number' && current.weekly.every(w => !!w.done);
  const shares = split
    ? cumulativeShares(current.weekly, current.done!, current.total, 'done')
    : cumulativeShares(current.weekly, current.reported, current.total);
  const prevSplit = !!previous && typeof previous.done === 'number' && previous.weekly.every(w => !!w.done);
  const prev = previous
    ? prevSplit
      ? cumulativeShares(previous.weekly, previous.done!, previous.total, 'done')
      : cumulativeShares(previous.weekly, previous.reported, previous.total)
    : [];
  // Ряды выровнены по дню от начала цикла: последняя неделя одного ряда —
  // последняя неделя другого. Сопоставляем с конца.
  const offset = prev.length - shares.length;
  return current.weekly.map((w, i) => ({
    week: w.week_to,
    weekFrom: w.week_from,
    weekReported: value(w.reported),
    weekDone: split ? value(w.done) : value(w.reported),
    weekInProgress: split ? (() => {
      const all = value(w.reported);
      const done = value(w.done);
      return all === null || done === null ? null : Math.max(0, all - done);
    })() : null,
    share: shares[i] ?? null,
    prevShare: prev[i + offset] ?? null,
    open: w.week_to > current.asOf,
  }));
}

/**
 * Ряд «Ход работ сезона» в гектарах (G1, бывш. 1ed): накопленная доля
 * выполненной площади работ — ГОТОВАЯ с сервера (`done_area_pct`, от итога
 * среза назад: последняя точка = главный «Выполнено»), столбцы — га работ,
 * впервые выполненных за неделю. Клиент не накапливает. `null` — сервер ряда
 * в гектарах не дал (площадь не указана): экран остаётся на счёте.
 */
export function seasonAreaSeries(
  current: { weekly: readonly WeekRow[]; asOf: string },
  previous?: { weekly: readonly WeekRow[] } | null,
): SeasonPoint[] | null {
  const hasArea = (rows: readonly WeekRow[]) => rows.length > 0 && rows.every(w => typeof w.done_area_pct === 'number');
  if (!hasArea(current.weekly)) return null;
  const prev = previous && hasArea(previous.weekly) ? previous.weekly : [];
  const offset = prev.length - current.weekly.length;
  return current.weekly.map((w, i) => ({
    week: w.week_to,
    weekFrom: w.week_from,
    weekReported: null,
    weekDone: typeof w.done_area_microha === 'number' ? w.done_area_microha / 1e6 : null,
    weekInProgress: null,
    share: w.done_area_pct ?? null,
    prevShare: prev[i + offset]?.done_area_pct ?? null,
    open: w.week_to > current.asOf,
  }));
}

/* ------------------------------------------------------------ темп кампании */

export interface PacePoint {
  day: string;
  /** Обязательств, ВПЕРВЫЕ отчитавшихся в этот день (любой отчёт). */
  reported: number | null;
  /** Обязательств, чей первый отчёт «выполнено» датирован этим днём. */
  done: number | null;
  /** Выполнено, накопленно (agroai-6al.35); без разреза — отчитались. */
  cumulative: number | null;
  /** В процессе на этот день: отчитались накопленно − выполнено накопленно. */
  inProgress: number | null;
  /** Линия «успеть к концу окна»: от накопленного на дату среза до итога. */
  need: number | null;
}

export interface CampaignPace {
  remaining: number;
  /** `null` — окно кампании не названо. */
  daysLeft: number | null;
  /** Обязательств в день, чтобы закрыть к концу окна; `null` — окно закрыто. */
  needPerDay: number | null;
  /** Средний темп последних 7 дней; `null` — дневного ряда нет. */
  pace7: number | null;
  series: PacePoint[];
  /** Единица ряда: га работ (сервер дал площадь) или работы (обязательства). */
  unit?: 'ha' | 'count';
  /** Итог кампании в единице ряда (знаменатель «из N»). */
  total?: number;
}

/**
 * Темп кампании в гектарах работ (G1, бывш. 1ed): прирост дня и накопленное —
 * ГОТОВЫЕ с сервера (`area_done_microha`, `area_done_cum_microha`, последняя
 * точка = «выполнено» кампании). Здесь только перевод в га и линия «успеть к
 * концу окна». `null` — сервер площади не дал: экран остаётся на счёте работ.
 */
export function campaignAreaPace(
  campaign: { area_work?: { total_microha: number | null; done_microha: number | null } | null; window_to: string | null; days_left: number | null },
  daily: readonly DayRow[] | null | undefined,
  asOf: string,
): CampaignPace | null {
  const total = campaign.area_work?.total_microha;
  const done = campaign.area_work?.done_microha;
  const days = (daily ?? []).filter(d => d.day <= asOf);
  if (typeof total !== 'number' || !(total > 0) || typeof done !== 'number') return null;
  if (!days.length || days.some(d => typeof d.area_done_cum_microha !== 'number')) return null;
  const ha = (v: number) => v / 1e6;
  const remaining = Math.max(0, ha(total) - ha(done));
  const daysLeft = campaign.days_left;
  const last7 = days.slice(-7).map(d => ha(d.area_done_microha ?? 0));
  const series: PacePoint[] = days.map(d => ({
    day: d.day,
    reported: null,
    done: ha(d.area_done_microha ?? 0),
    cumulative: ha(d.area_done_cum_microha!),
    inProgress: null,
    need: null,
  }));
  const lastPoint = series[series.length - 1]!;
  lastPoint.need = lastPoint.cumulative;
  const span = campaign.window_to ? daysBetween(lastPoint.day, campaign.window_to) : 0;
  for (let k = 1; k <= span; k += 1) {
    series.push({ day: addDays(lastPoint.day, k), reported: null, done: null, cumulative: null, inProgress: null, need: lastPoint.cumulative! + ((ha(total) - lastPoint.cumulative!) * k) / span });
  }
  return {
    remaining,
    daysLeft,
    needPerDay: daysLeft !== null && daysLeft > 0 ? remaining / daysLeft : null,
    pace7: last7.reduce((a, b) => a + b, 0) / last7.length,
    series,
    unit: 'ha',
    total: ha(total),
  };
}

/**
 * Темп кампании по дням. Ось — «выполнено» (agroai-6al.35): остаток и «нужно
 * в день» считаются до финального отчёта, а «в процессе» идёт отдельной
 * величиной (отчитались накопленно − выполнено накопленно). Сервер без
 * разреза по степени — прежняя ось «отчитались».
 */
export function campaignPace(
  campaign: { obligations: Progress; window_to: string | null; days_left: number | null },
  daily: readonly DayRow[] | null | undefined,
  asOf: string,
): CampaignPace {
  const { total, reported } = campaign.obligations;
  const days = (daily ?? []).filter(d => d.day <= asOf);
  const split = hasProgressSplit(campaign.obligations) && days.every(d => !!d.done);
  const doneTotal = split ? doneOf(campaign.obligations) : reported;
  const remaining = Math.max(0, total - doneTotal);
  const daysLeft = campaign.days_left;
  const needPerDay = daysLeft !== null && daysLeft > 0 ? remaining / daysLeft : null;
  const firsts = days.map(d => value(d.reported));
  const counts = split ? days.map(d => value(d.done)) : firsts;
  const known = counts.every(c => c !== null) && firsts.every(c => c !== null);
  const last7 = counts.slice(-7);
  const pace7 = days.length && known ? (last7 as number[]).reduce((a, b) => a + b, 0) / last7.length : null;

  const series: PacePoint[] = [];
  if (days.length && known) {
    let acc = doneTotal - (counts as number[]).reduce((a, b) => a + b, 0);
    let accAll = reported - (firsts as number[]).reduce((a, b) => a + b, 0);
    days.forEach((d, i) => {
      acc += counts[i]!;
      accAll += firsts[i]!;
      series.push({
        day: d.day,
        reported: firsts[i]!,
        done: counts[i]!,
        cumulative: acc,
        inProgress: split ? Math.max(0, accAll - acc) : null,
        need: null,
      });
    });
    const last = series[series.length - 1]!;
    last.need = last.cumulative;
    const span = campaign.window_to ? daysBetween(last.day, campaign.window_to) : 0;
    for (let k = 1; k <= span; k += 1) {
      series.push({
        day: addDays(last.day, k),
        reported: null,
        done: null,
        cumulative: null,
        inProgress: null,
        need: last.cumulative! + ((total - last.cumulative!) * k) / span,
      });
    }
  }
  return { remaining, daysLeft, needPerDay, pace7, series };
}

/* ------------------------------------------------------------ тепловая карта */

export interface HeatCell {
  stageId: string;
  reported: number;
  total: number;
  /** `null` — работ стадии на массиве не было (штриховка), не «0 %». */
  share: number | null;
}

export interface HeatRow {
  massifId: number;
  name: string;
  cells: HeatCell[];
}

export function heatRows(
  rows: readonly MassifStageRow[],
  massifs: ReadonlyArray<{ id: number; name: string }>,
  stages: ReadonlyArray<{ id: string }>,
): HeatRow[] {
  const byKey = new Map(rows.map(r => [`${r.massif_id}|${r.stage_id}`, r]));
  return [...massifs]
    .map(m => ({ massifId: m.id, name: massifLabel(m.name) }))
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'))
    .map(m => ({
      ...m,
      cells: stages.map(s => {
        const r = byKey.get(`${m.massifId}|${s.id}`);
        const ob = r?.present ? r.obligations : null;
        return {
          stageId: s.id,
          reported: ob?.reported ?? 0,
          total: ob?.total ?? 0,
          share: ob ? sharePct(ob.reported, ob.total) : null,
        };
      }),
    }));
}

/** Ступень последовательной шкалы 1..5 (0–20–40–60–80–100 %). */
export function heatStep(share: number): 1 | 2 | 3 | 4 | 5 {
  return Math.min(5, Math.max(1, Math.floor(share / 20) + 1)) as 1 | 2 | 3 | 4 | 5;
}

/* ------------------------------------------------------------ ранжирование */

export interface RankedFarm {
  id: number;
  name: string;
  massifId: number | null;
  massif: string;
  overdue: number;
  total: number;
  share: number;
  /** Земля хозяйства в цикле, га; `null` — не измерена. */
  areaHa: number | null;
  /** Медиана доли среди хозяйств того же массива (тот же порог). */
  massifMedian: number | null;
}

/**
 * Хозяйства по «после срока без отчёта». В ранжирование входят только
 * хозяйства с ≥ MIN_OBLIGATIONS обязательств; у строки без названной величины
 * просрочки доли нет, и она в ранжирование не попадает.
 *
 * Порядок `volume` — по ЧИСЛУ обязательств после срока (масштаб: обязательство
 * = работа на контуре, у крупного хозяйства их больше), `most`/`least` — по
 * доле. Доля одна поднимала наверх хозяйства на 7–40 га: топ-10 по доле на
 * demo4 — 243 га, 2 % земли хлопка, а хозяйство на 219 га с 91 обязательством
 * после срока в топ не попадало (порог №67 «≥30 обязательств» мелочь не
 * отсекает: у одного контура ~37 работ). Обзор района берёт `volume` и
 * показывает долю и землю рядом (07.10.2026, design-rd).
 */
export function rankFarms(
  rows: readonly FarmRow[],
  order: 'most' | 'least' | 'volume',
  limit = 10,
  min = MIN_OBLIGATIONS,
): { rows: RankedFarm[]; eligible: number } {
  const eligible: RankedFarm[] = [];
  for (const r of rows) {
    const total = r.totals.obligations.total;
    const overdue = value(r.totals.overdue);
    if (total < min || overdue === null) continue;
    eligible.push({
      id: r.id,
      name: farmLabel(r.name),
      massifId: r.massif?.id ?? null,
      massif: massifLabel(r.massif?.canonical_name ?? null),
      overdue,
      total,
      share: (100 * overdue) / total,
      areaHa: r.area?.total_microha == null ? null : r.area.total_microha / 1e6,
      massifMedian: null,
    });
  }
  const byMassif = new Map<number, number[]>();
  for (const f of eligible) {
    if (f.massifId === null) continue;
    const list = byMassif.get(f.massifId) ?? [];
    list.push(f.share);
    byMassif.set(f.massifId, list);
  }
  for (const f of eligible) f.massifMedian = f.massifId === null ? null : median(byMassif.get(f.massifId) ?? []);
  const sorted = [...eligible].sort((a, b) =>
    order === 'volume' ? b.overdue - a.overdue || b.share - a.share
      : order === 'most' ? b.share - a.share || b.overdue - a.overdue : a.share - b.share || b.total - a.total,
  );
  return { rows: sorted.slice(0, limit), eligible: eligible.length };
}

/** Порог ранжирования по площади (аудит KPI 3.5): земля хозяйства под работами цикла, га. */
export const MIN_AREA_HA = 10;

export interface AreaRankedFarm {
  id: number;
  name: string;
  massif: string;
  /** ЗЕМЛЯ хозяйства (каждый гектар один раз), где есть работа после срока без отчёта (`by_farm[].overdue_area`). */
  overdueHa: number;
  /** Та же земля, % земли хозяйства под работами цикла. */
  overduePct: number | null;
  /** Гектары работ после срока (сумма площадей работ) — второй слой мелко. */
  overdueWorkHa: number | null;
  /** Счёт: работ после срока и всего — второй слой. */
  overdue: number | null;
  total: number;
  /** Земля хозяйства под работами цикла, га. */
  areaHa: number | null;
}

/**
 * «Где ущерб» (аудит KPI 3.5, решения №645, №690): хозяйства по ЗЕМЛЕ, где
 * есть работа после срока без отчёта, — каждый гектар один раз, поэтому число
 * сравнимо с землёй хозяйства и никогда её не превышает (сумма гектаров работ
 * превышала: 1 535 «га» у хозяйства на 219 га). В ранжирование входят
 * хозяйства с землёй под работами цикла ≥ MIN_AREA_HA и названной землёй
 * после срока; без неё места нет (не «0 га»). Равные — по гектарам работ.
 */
export function rankFarmsByArea(
  rows: readonly FarmRow[],
  limit = 10,
  minHa = MIN_AREA_HA,
): { rows: AreaRankedFarm[]; eligible: number } {
  const eligible: AreaRankedFarm[] = [];
  for (const r of rows) {
    const land = r.area?.total_microha == null ? null : r.area.total_microha / 1e6;
    const overLand = value(r.overdue_area);
    if (overLand === null || land === null || land < minHa) continue;
    const aw = r.area_work;
    const overdueHa = overLand / 1e6;
    eligible.push({
      id: r.id,
      name: farmLabel(r.name),
      massif: massifLabel(r.massif?.canonical_name ?? null),
      overdueHa,
      overduePct: land > 0 ? (100 * overdueHa) / land : null,
      overdueWorkHa: aw && aw.overdue_microha !== null ? aw.overdue_microha / 1e6 : null,
      overdue: value(r.totals.overdue),
      total: r.totals.obligations.total,
      areaHa: land,
    });
  }
  const sorted = [...eligible].sort((a, b) => b.overdueHa - a.overdueHa || (b.overdueWorkHa ?? 0) - (a.overdueWorkHa ?? 0));
  return { rows: sorted.slice(0, limit), eligible: eligible.length };
}

/* ------------------------------------------------------------ вода */

const m3 = (s: string | null): number | null => (s === null ? null : Number(s));

export interface WaterHotspot {
  key: string;
  label: string;
  requested: number;
  delivered: number;
  share: number;
}

export function waterFacts(rows: readonly WaterRow[]) {
  let deficit = 0;
  let known = false;
  const hotspots: WaterHotspot[] = [];
  for (const r of rows) {
    const req = m3(r.requested_m3);
    const del = m3(r.delivered_m3);
    if (req === null || del === null) continue;
    known = true;
    deficit += Math.max(0, req - del);
    if (req > 0) {
      const share = (100 * del) / req;
      if (share < WATER_HOTSPOT_PCT) hotspots.push({ key: r.key, label: r.label ?? r.key, requested: req, delivered: del, share });
    }
  }
  hotspots.sort((a, b) => a.share - b.share);
  return { deficit: known ? deficit : null, hotspots: hotspots.slice(0, 3) };
}

/* ------------------------------------------------------------ карантин */

export interface QuarantineFocus {
  massif: string;
  red: number;
  yellow: number;
  overdueRequirements: number;
  contours: string[];
}

export function quarantineFoci(items: readonly QRow[]): QuarantineFocus[] {
  const map = new Map<string, QuarantineFocus>();
  for (const it of items) {
    if (it.status !== 'red' && it.status !== 'yellow') continue;
    const name = massifLabel(it.massif?.canonical_name ?? null);
    const f = map.get(name) ?? { massif: name, red: 0, yellow: 0, overdueRequirements: 0, contours: [] };
    if (it.status === 'red') f.red += 1;
    else f.yellow += 1;
    if (it.requirement_overdue) f.overdueRequirements += 1;
    f.contours.push(it.contour_no ?? String(it.contour_id));
    map.set(name, f);
  }
  // Очаги — по массе, а не рейтинг массивов: равные идут по алфавиту.
  return [...map.values()].sort(
    (a, b) => b.red - a.red || b.red + b.yellow - (a.red + a.yellow) || a.massif.localeCompare(b.massif, 'ru'),
  );
}

/* ------------------------------------------------------------ поручения */

export interface AssignmentEffect {
  reported: number | null;
  overdue: number | null;
}

/** Эффект из diff: прибавка отчётов и убыль просрочки. */
export function effectOf(rows: ReadonlyArray<{ field: string; delta: number | null }> | null | undefined): AssignmentEffect {
  const pick = (f: string) => rows?.find(r => r.field === f)?.delta ?? null;
  return { reported: pick('reported'), overdue: pick('overdue') };
}

export const hasEffect = (e: AssignmentEffect | undefined) =>
  !!e && ((e.reported ?? 0) > 0 || (e.overdue ?? 0) < 0);

export const isClosed = (a: Pick<Assignment, 'status'>) => a.status === 'checked';

/** Поручение, как его читает правило «после срока»: поле `overdue` ответа необязательно (прежний сервер его не знал). */
export type AssignmentDue = Pick<Assignment, 'status' | 'due'> & { overdue?: boolean | null };

/**
 * Поручение после срока. Правило считает СЕРВЕР на дату среза ответа
 * (`overdue` в строке: «не проверено и срок раньше среза»), и клиент читает
 * его как есть. Прежнее правило остаётся запасным — только для ответа без
 * этого поля — и считается на дате среза (`today`: `useSliceDate()`, а не часы
 * браузера); пока дата среза неизвестна (`null`), просрочку утверждать нечем.
 */
export const isLate = (a: AssignmentDue, today: string | null): boolean => {
  if (typeof a.overdue === 'boolean') return a.overdue;
  return !isClosed(a) && !!a.due && !!today && a.due < today;
};

/** Порядок строк: после срока → с эффектом → прочие; внутри — по сроку. */
export function orderAssignments<T extends AssignmentDue & Pick<Assignment, 'id'>>(
  items: readonly T[],
  effects: ReadonlyMap<number, AssignmentEffect>,
  today: string | null,
): T[] {
  const rank = (a: T) => (isLate(a, today) ? 0 : hasEffect(effects.get(a.id)) ? 1 : 2);
  return [...items].sort((a, b) => rank(a) - rank(b) || (a.due ?? '9999').localeCompare(b.due ?? '9999') || b.id - a.id);
}
